/**
 * URL friendly per schede prodotto: /prodotti/marca-modello-misura-{id}
 * - Slug da brand, name, size per SEO e leggibilità
 * - ID finale: short_id numerico (es. 123456) o, in fallback, ObjectId (24 hex)
 */

const SLUG_MAX_LENGTH = 60;
const OBJECT_ID_LENGTH = 24;
const OBJECT_ID_REGEX = /[a-f0-9]{24}$/i;
const SHORT_ID_REGEX = /^\d+$/;

/**
 * Normalizza una stringa per l'uso in URL: minuscolo, senza accenti, spazi e caratteri speciali → trattini
 */
function slugify(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/, "");
}

/**
 * Costruisce il segmento URL per un prodotto (slug + short_id o id).
 * @param {{ id: string, short_id?: number, brand?: string, name?: string, size?: string }} product
 * @returns {string} es. "michelin-pilot-sport-4-205-55-r16-123456"
 */
export function productSlugSegment(product) {
  const id = product?.short_id != null ? String(product.short_id) : product?.id;
  if (!id) return product?.id || "";
  const parts = [product.brand, product.name, product.size].filter(Boolean);
  const slug = slugify(parts.join(" ")) || "pneumatico";
  return `${slug}-${id}`;
}

/**
 * Restituisce il path completo per la scheda prodotto (per Link e canonical).
 * @param {{ id: string, brand?: string, name?: string, size?: string }} product
 * @returns {string} es. "/prodotti/michelin-pilot-sport-4-69aed40bc89de40cbcbce7d9"
 */
export function productDetailPath(product) {
  const segment = productSlugSegment(product);
  return segment ? `/prodotti/${segment}` : `/prodotti/${product?.id || ""}`;
}

/**
 * Estrae l'ID prodotto dal parametro della route (slug-id o solo id).
 * Restituisce short_id (numerico) o ObjectId (24 hex) da passare all'API.
 * Compatibile con: /prodotti/michelin-pilot-4-123456, /prodotti/123456, /prodotti/69aed40bc89de40cbcbce7d9
 * @param {string} slugOrId - valore di useParams().slugOrId
 * @returns {string|null} short_id (es. "123456") o ObjectId (24 hex) o null
 */
export function productIdFromParam(slugOrId) {
  if (!slugOrId) return null;
  const trimmed = slugOrId.trim();
  if (trimmed.length === OBJECT_ID_LENGTH && /^[a-f0-9]{24}$/i.test(trimmed)) {
    return trimmed;
  }
  const lastSegment = trimmed.includes("-") ? trimmed.split("-").pop() : trimmed;
  if (SHORT_ID_REGEX.test(lastSegment)) return lastSegment;
  const match = trimmed.match(OBJECT_ID_REGEX);
  return match ? match[0] : null;
}
